// EventNudge — the peripheral-vision meeting reminder (BC5 adaptation): when a
// calendar event starts within 15 minutes, a small pill rises at the bottom of
// the screen with the time-to-go, an instant-join button when the event has a
// video link, and a tint that shifts greener as the start closes in. Dismissal
// is per event, remembered for the session.

import { useEffect, useMemo, useState } from 'react'
import { CalendarClock, Video, X } from 'lucide-react'
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar'

const DISMISS_KEY = 'chatrone.eventNudge.dismissed'

const dismissed = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]')) } catch { return new Set() }
}
const dismiss = (id) => {
  try {
    const s = dismissed(); s.add(id)
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...s]))
  } catch { /* session nicety */ }
}

function joinLink(event) {
  if (event.hangoutLink) return event.hangoutLink
  const ep = event.conferenceData?.entryPoints?.find(e => e.entryPointType === 'video')
  if (ep?.uri) return ep.uri
  if (/^https?:\/\//.test(event.location || '')) return event.location
  return null
}

export default function EventNudge() {
  const { events } = useGoogleCalendar()
  const [now, setNow] = useState(Date.now())
  const [hidden, setHidden] = useState(() => dismissed())

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const next = useMemo(() => {
    const soon = (events || [])
      .filter(e => e.start?.dateTime && !hidden.has(e.id))
      .map(e => ({ e, ms: new Date(e.start.dateTime).getTime() - now }))
      .filter(({ ms }) => ms > -60_000 && ms <= 15 * 60_000)
      .sort((a, b) => a.ms - b.ms)
    return soon[0] || null
  }, [events, now, hidden])

  if (!next) return null
  const mins = Math.max(0, Math.round(next.ms / 60_000))
  const link = joinLink(next.e)
  // The closer it gets, the greener it reads.
  const tone = mins <= 2
    ? 'bg-green-100 border-green-400 text-green-900'
    : mins <= 5
      ? 'bg-green-50 border-green-300 text-green-800'
      : mins <= 10
        ? 'bg-sage-50 border-sage-300 text-sage-800'
        : 'bg-white border-warm-300 text-warm-700'

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 animate-flow-in" role="status">
      <div className={`flex items-center gap-2.5 rounded-full border-2 pl-3.5 pr-2 py-1.5 shadow-lg backdrop-blur-sm ${tone}`}>
        <CalendarClock size={15} className={mins <= 2 ? 'animate-pulse' : ''} />
        <span className="text-[12.5px] font-semibold max-w-[280px] truncate">{next.e.summary || 'Upcoming event'}</span>
        <span className="text-[12px] font-medium opacity-80 whitespace-nowrap">
          {mins <= 0 ? 'starting now' : `in ${mins} min`}
        </span>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-600 text-white text-[11.5px] font-bold hover:bg-green-700 transition-colors"
          >
            <Video size={12} /> Join
          </a>
        )}
        <button
          onClick={() => { dismiss(next.e.id); setHidden(dismissed()) }}
          className="p-1 rounded-full opacity-60 hover:opacity-100"
          aria-label="Dismiss reminder"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  )
}
